import { SearchInput } from '@legacy/components/ui-components/SearchInput';
import { IconButton } from '@legacy/components/ui-components/IconButton';
import cx from 'classnames';
import { useNavigate } from 'react-router';
import {
  ChangeEvent,
  RefObject,
  useState,
  useRef,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getKeyboardFocusableElements } from '@legacy/utils/accessibility';
import {
  blurSelectElement,
  reloadKeyboardSelectedElement,
  keyboardAction,
} from '@legacy/utils/keyboardUpDownEvent';
import { shortcutElementNames } from '@legacy/components/global/ShortcutSystem';
import { useResetInputEvent } from './SearchHookHelper';
import { SearchResultPanel } from './components/SearchResultPanel';
import SearchCategoryPanel from './components/SearchCategoryPanel';
import styles from './Search.module.scss';
import { useSearchStore } from '@/stores/search';
import { Paper } from '@mui/material';

interface SearchProps {
  inputRef: RefObject<HTMLInputElement | null>;
  searchFilterRef: RefObject<HTMLDivElement | null>;
  selectItemIndex: number;
  setSelectItemIndex: Dispatch<SetStateAction<number>>;
  searchValueTesting?: string;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
}

// eslint-disable-next-line react/display-name
export default ({
  inputRef,
  searchFilterRef,
  selectItemIndex = -1,
  setSelectItemIndex,
  searchValueTesting = '',
  setSearchValue,
  searchValue,
}: SearchProps) => {
  const { t } = useTranslation('header');
  const navigate = useNavigate();
  const [isPauseSearch, setIsPauseSearch] = useState<boolean>(false);
  const searchContainer = useRef<HTMLDivElement>(null);
  const searchFiltersButton = useRef<HTMLButtonElement>(null);
  const searchContainerResults = useRef<HTMLUListElement>(null);
  const { isCategoryOpen, hasActiveCategoryFilters, updateStore } =
    useSearchStore();

  if (!searchValueTesting) useResetInputEvent(setSearchValue);
  if (searchValueTesting) setSearchValue(searchValueTesting);

  const resetSearch = () => {
    updateStore({
      type: 'RESET_SEARCH',
    });
  };

  const toggleSearchCategory = () =>
    updateStore({
      type: 'TOGGLE_CATEGORY',
    });

  const onItemSelect = useCallback(() => {
    resetSearch();
    setSearchValue('');
  }, [resetSearch, setSearchValue]);

  const focusInput = useCallback(() => {
    updateStore({
      type: 'CLOSE_CATEGORY',
    });
    inputRef.current?.focus();
  }, [inputRef]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e?.target?.value);
    setIsPauseSearch(true);
    setTimeout(() => setIsPauseSearch(false), 500);
  };

  const onKeyDown = (event: any) => {
    const focusableElements = getKeyboardFocusableElements(
      searchContainerResults?.current,
    );
    const currentSelectedItem = focusableElements[
      selectItemIndex
    ] as HTMLElement;
    if (focusableElements.length > 0) {
      const isNeedToBlur = keyboardAction({
        event,
        setSelectItemIndex,
        selectItemIndex,
        onItemSelect,
        navigate,
        focusableElements,
      });
      if (isNeedToBlur) blurSelectElement(currentSelectedItem);
    }
  };

  const onFocusHandle = () => {
    updateStore({
      type: 'CLOSE_CATEGORY',
    });
  };

  reloadKeyboardSelectedElement(
    getKeyboardFocusableElements(searchContainerResults?.current)?.[
      selectItemIndex
    ],
    inputRef,
  );

  const filterIsActive = hasActiveCategoryFilters();

  return (
    <Paper
      ref={searchContainer}
      role="search"
      data-automation-id="SearchContainer"
    >
      <div className={styles.Search}>
        <div className={styles.SearchInput}>
          <SearchInput
            label={t('SearchInputLabel')}
            name={shortcutElementNames.mainSearch}
            hiddenLabel
            placeholder={t('SearchInputPlaceholder')}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            onFocus={onFocusHandle}
            value={searchValue}
            ref={inputRef}
            autoComplete="off"
            data-automation-id="SuperSearchInput"
            aria-autocomplete="none"
            clearInputText={t('ClearSearchInputButton')}
            clearInput={() => {
              setSearchValue('');
            }}
            noBorderRadiusRight
          />
        </div>

        <IconButton
          data-automation-id="SuperSearchCategoryButton"
          onClick={toggleSearchCategory}
          iconName="filter"
          border
          className={cx(styles.SearchCategoryButton, {
            [styles.SearchCategoryButtonHasActiveFilter]: filterIsActive,
          })}
          aria-expanded={isCategoryOpen}
          aria-controls="MainSearchCategoryFilter"
          tooltipText={t('SearchCategoriesFilterButton')}
          ref={searchFiltersButton}
        />
      </div>
      <SearchResultPanel
        isPauseSearch={isPauseSearch}
        searchValue={searchValue}
        onItemSelect={onItemSelect}
        searchContainerResults={searchContainerResults}
      />
      <SearchCategoryPanel
        searchFilterCategoriesRef={searchFilterRef}
        focusInput={focusInput}
      />
    </Paper>
  );
};
